/**
 * @file     useNavigationCategories.ts
 * @purpose  Returns navbar/footer navigation with deal-finder items remapped
 *           to "View matches" → /dashboard/search/saved when the
 *           authenticated user has already submitted the funnel.
 */
'use client';

import { useMemo } from 'react';
import {
  navigationCategories as defaultCategories,
  type NavigationCategory,
} from '@/core/constants/navigation';
import { useAuth } from '@/hooks/useAuth';
import {
  DEAL_FINDER_SUBMITTED_DESTINATION,
  DEAL_FINDER_SUBMITTED_LABEL_KEY,
  isDealFinderFunnelPath,
} from '@/core/config/ctaState';

function isBuyerFunnelPath(path: string): boolean {
  if (!isDealFinderFunnelPath(path)) return false;
  if (path.includes("flow=seller") || path.includes("flow=landlord")) return false;
  return true;
}

function remapCategories(categories: readonly NavigationCategory[]): NavigationCategory[] {
  return categories.map((cat) => ({
    ...cat,
    directLink: cat.directLink && isBuyerFunnelPath(cat.directLink)
      ? DEAL_FINDER_SUBMITTED_DESTINATION
      : cat.directLink,
    items: cat.items.map((item) =>
      isBuyerFunnelPath(item.path)
        ? { ...item, path: DEAL_FINDER_SUBMITTED_DESTINATION, labelKey: DEAL_FINDER_SUBMITTED_LABEL_KEY }
        : item,
    ),
  }));
}

export function useNavigationCategories(
  source: readonly NavigationCategory[] = defaultCategories,
): NavigationCategory[] {
  const { hasSubmittedDealFinder } = useAuth();
  return useMemo(
    () => (hasSubmittedDealFinder ? remapCategories(source) : [...source]),
    [hasSubmittedDealFinder, source],
  );
}
